import { useState, type ReactNode } from 'react';
import styles from './leftRail.module.css';

type IExpandableSectionProps = {
  title: string;
  /** Count rendered next to the title — `Professions (19)`. */
  count: number;
  /** Default-open on first render. */
  defaultOpen?: boolean;
  /** Optional copy when the section's body is empty. Hidden when null. */
  emptyText?: string | null;
  /** Right-aligned slot rendered next to the toggle (e.g. a "Clear (n)"
   *  link). Lives outside the toggle `<button>` to avoid nesting buttons. */
  actions?: ReactNode;
  children: ReactNode;
};

/**
 * Header + collapsible body — drives the three sections in the LeftRail
 * Workforce tab (Professions / Managers / Equipment). Mirrors
 * trusstor_dashboard's `ExpandableComponent`: chevron rotates on open, count
 * lives in the header. We render children unconditionally and use `hidden`
 * on the body wrapper so opening doesn't re-mount the rows (which would
 * lose their internal state if they ever grow any).
 */
export function ExpandableSection({
  title,
  count,
  defaultOpen = false,
  emptyText = null,
  actions = null,
  children,
}: IExpandableSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className={styles.section}>
      <div className={styles.sectionHeaderRow}>
        <button
          type="button"
          className={styles.sectionHeader}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <span
            className={`${styles.sectionChevron} ${open ? styles.sectionChevronOpen : ''}`}
            aria-hidden="true"
          >
            <ChevronIcon />
          </span>
          <span className={styles.sectionTitle}>{title}</span>
        </button>
        {actions && <div className={styles.sectionActions}>{actions}</div>}
        <span className={styles.sectionCount}>({count})</span>
      </div>
      <div className={styles.sectionBody} hidden={!open}>
        {count === 0 && emptyText ? (
          <div className={styles.sectionEmpty}>{emptyText}</div>
        ) : (
          children
        )}
      </div>
    </section>
  );
}

function ChevronIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M6 9l6 6 6-6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
